import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { NavLink, useNavigate } from 'react-router';
import navigationStyles from '~/styles/myEnvironment/navigation.module.css';
import myEnvironmentStyles from '~/styles/myEnvironment/my-environment.module.css';
import contentStyles from '~/styles/myEnvironment/content.module.css';
import utilStyles from '~/styles/utils.module.css';
import useTranslation from '~/hooks/useTranslation';
import useAuthInterceptor from '~/hooks/useAuthInterceptor';
import myEnvironmentRoutes from '~/routes/myEnvironment';
import { removeSanctumToken } from '~/helpers/sanctumAuth';
import IconChevronUp from '~icons/tabler/chevron-up';
import IconChevronDown from '~icons/tabler/chevron-down';
import IconLogout from '~icons/tabler/logout';

type Props = {
  children: React.ReactNode;
};

export default function MyEnvironmentLayout({ children }: Props) {
  const translate = useTranslation();
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const navigationListId = useId();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  useAuthInterceptor();

  useEffect(() => {
    if (!containerRef.current) {
      return () => {};
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const container = entry.target as HTMLDivElement;

      if (!isNavigationOpen && container.clientWidth >= 768) {
        setIsNavigationOpen(true);
      }
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isNavigationOpen, containerRef]);

  const onLogout = useCallback(() => {
    removeSanctumToken();
    navigate(myEnvironmentRoutes.LOGIN);
  }, [navigate]);

  const navItems = [
    {
      to: myEnvironmentRoutes.RESERVATIONS,
      label: translate('myEnvironment.reservations.title'),
    },
  ];

  return (
    <div className={myEnvironmentStyles.container} ref={containerRef}>
      <div className={myEnvironmentStyles.row}>
        <nav className={myEnvironmentStyles.navigation}>
          <div className={navigationStyles.navigation}>
            <button
              type="button"
              className={navigationStyles.navigationHeader}
              onClick={() => setIsNavigationOpen(!isNavigationOpen)}
              aria-expanded={isNavigationOpen}
              aria-controls={navigationListId}
            >
              <h2 className={navigationStyles.navigationTitle}>
                {translate('myEnvironment.navigation_title')}
              </h2>

              <div className={navigationStyles.navigationButton}>
                {isNavigationOpen ? <IconChevronUp /> : <IconChevronDown />}
                <span className={utilStyles.srOnly}>
                  {
                    isNavigationOpen
                      ? translate('myEnvironment.close_navigation')
                      : translate('myEnvironment.open_navigation')
                  }
                </span>
              </div>
            </button>

            <ul
              className={navigationStyles.navigationList}
              aria-hidden={!isNavigationOpen}
              id={navigationListId}
            >
              {navItems.map((item) => (
                <li className={navigationStyles.navigationItem} key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) => `
                      ${navigationStyles.navigationLink}
                      ${isActive ? navigationStyles.navigationLinkActive : ''}
                    `}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}

              <li className={navigationStyles.navigationItem}>
                <button
                  type="button"
                  className={navigationStyles.navigationLink}
                  onClick={onLogout}
                >
                  <IconLogout />
                  {translate('myEnvironment.logout')}
                </button>
              </li>
            </ul>
          </div>
        </nav>

        <div className={myEnvironmentStyles.content}>
          <div className={contentStyles.content}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
